import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogOut, Search, Download, Eye, X, Save, CheckCircle2, Clock, Archive, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi, adminLogout, verifyAdminToken, getAdminToken } from '../lib/adminAuth';

const PAYMENT_FILTERS = [
  { value: '', label: 'All' },
  { value: 'paid', label: 'Paid' },
  { value: 'initiated', label: 'Pending' },
  { value: 'not_started', label: 'Not Started' },
  { value: 'unpaid', label: 'Unpaid' },
];

const TYPE_FILTERS = [
  { value: '', label: 'All Types' },
  { value: 'momshiach_method', label: 'Momshiach Method' },
  { value: 'intake', label: 'Intake (1:1)' },
  { value: 'health_coaching', label: 'Health Coaching' },
  { value: 'carebnb', label: 'CareBnB' },
];

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', icon: Clock },
  { value: 'contacted', label: 'Contacted', icon: CheckCircle2 },
  { value: 'archived', label: 'Archived', icon: Archive },
];

const fmtMoney = (n, cur = 'ILS') =>
  `${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })} ${cur === 'ILS' ? 'NIS' : cur}`;

const PaymentBreakdown = ({ p }) => (
  <dl className="payment-breakdown" data-testid="admin-payment-breakdown">
    <dt>Price</dt><dd>{fmtMoney(p.price, p.currency)}{p.per_payment ? ` × ${p.installments} monthly` : ''}</dd>
    {p.addons.map((a) => (
      <React.Fragment key={a.name}><dt>+ {a.name}</dt><dd>{fmtMoney(a.price, p.currency)}</dd></React.Fragment>
    ))}
    {p.discount_pct > 0 && (
      <><dt>First-time discount ({Math.round(p.discount_pct * 100)}%)</dt><dd data-testid="admin-discount">−{fmtMoney(p.discount_amount, p.currency)}</dd></>
    )}
    <dt>Total</dt><dd>{fmtMoney(p.amount_due, p.currency)}</dd>
    <dt>Paid so far</dt><dd data-testid="admin-amount-paid"><strong>{fmtMoney(p.amount_paid, p.currency)}</strong></dd>
  </dl>
);

// Gift orders store the buyer under gifter* fields
const contactOf = (s) => {
  const d = s.data || {};
  return s.type === 'carebnb_gift'
    ? { ...d, firstName: d.gifterFirstName, lastName: d.gifterLastName, email: d.gifterEmail, phone: d.gifterPhone }
    : d;
};

const statusBadgeClass = (payment_status) => {
  switch (payment_status) {
    case 'paid': return 'status-paid';
    case 'initiated': return 'status-pending';
    default: return 'status-notstarted';
  }
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [quizStats, setQuizStats] = useState(null);

  const fetchQuizStats = useCallback(async () => {
    try {
      const { data } = await adminApi.get('/admin/quiz-analytics');
      setQuizStats(data);
    } catch (err) {
      // silent — not critical
    }
  }, []);

  const fetchSubmissions = useCallback(async (pFilter = paymentFilter, tFilter = typeFilter) => {
    try {
      setLoading(true);
      const params = {};
      if (pFilter) params.payment_status = pFilter;
      if (tFilter) params.type = tFilter;
      const { data } = await adminApi.get('/submissions', { params });
      setSubmissions(data.items || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        adminLogout();
        navigate('/admin/login');
      } else {
        toast.error('Failed to load submissions');
      }
    } finally {
      setLoading(false);
    }
  }, [paymentFilter, typeFilter, navigate]);

  useEffect(() => {
    (async () => {
      const ok = await verifyAdminToken();
      if (!ok) {
        navigate('/admin/login', { replace: true });
        return;
      }
      fetchSubmissions();
      fetchQuizStats();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchSubmissions(paymentFilter, typeFilter);
  }, [paymentFilter, typeFilter, fetchSubmissions]);

  const filtered = submissions.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const d = contactOf(s);
    return (
      (d.firstName || '').toLowerCase().includes(q) ||
      (d.lastName || '').toLowerCase().includes(q) ||
      (d.email || '').toLowerCase().includes(q) ||
      (d.phone || '').toLowerCase().includes(q) ||
      (s.id || '').toLowerCase().includes(q)
    );
  });

  const openDetails = (sub) => {
    setSelected(sub);
    setNotes(sub.admin_notes || '');
  };

  const closeDetails = () => {
    setSelected(null);
    setNotes('');
  };

  const updateStatus = async (newStatus) => {
    if (!selected) return;
    try {
      setSaving(true);
      const { data } = await adminApi.patch(`/submissions/${selected.id}`, {
        admin_status: newStatus,
      });
      setSubmissions((prev) => prev.map((s) => (s.id === data.id ? data : s)));
      setSelected(data);
      toast.success(`Marked as ${newStatus}`);
    } catch {
      toast.error('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const saveNotes = async () => {
    if (!selected) return;
    try {
      setSaving(true);
      const { data } = await adminApi.patch(`/submissions/${selected.id}`, { admin_notes: notes });
      setSubmissions((prev) => prev.map((s) => (s.id === data.id ? data : s)));
      setSelected(data);
      toast.success('Notes saved');
    } catch {
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (submissionId) => {
    try {
      const { data } = await adminApi.get(`/submissions/${submissionId}/pdf`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `momshiach_submission_${submissionId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('PDF download failed');
    }
  };

  const handleLogout = () => {
    adminLogout();
    navigate('/admin/login');
  };

  const photoUrl = (photoId) => {
    if (!photoId) return null;
    const token = getAdminToken();
    return `${process.env.REACT_APP_BACKEND_URL}/api/uploads/photo/${photoId}?auth=${token}`;
  };

  return (
    <div className="admin-page" data-testid="admin-dashboard-page">
      {/* Header */}
      <header className="admin-header">
        <div>
          <Link to="/">
            <img src="/brand/logo.png" alt="Momshiach" className="logo-img" />
          </Link>
          <span className="admin-badge">Admin</span>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={() => fetchSubmissions()}
            className="btn-secondary btn-sm"
            data-testid="admin-refresh-btn"
          >
            <RefreshCw size={16} />
            <span>Refresh</span>
          </button>
          <button onClick={handleLogout} className="btn-secondary btn-sm" data-testid="admin-logout-btn">
            <LogOut size={16} />
            <span>Log Out</span>
          </button>
        </div>
      </header>

      {/* Quiz Analytics */}
      {quizStats && quizStats.total > 0 && (
        <section className="quiz-analytics-panel" data-testid="quiz-analytics-panel">
          <div className="quiz-analytics-header">
            <div>
              <p className="quiz-analytics-eyebrow">Ecosystem Quiz</p>
              <h3 className="quiz-analytics-title">{quizStats.total} completion{quizStats.total === 1 ? '' : 's'}</h3>
            </div>
            <button onClick={fetchQuizStats} className="btn-secondary btn-sm" data-testid="quiz-analytics-refresh">
              <RefreshCw size={14} />
              <span>Refresh</span>
            </button>
          </div>
          <div className="quiz-analytics-grid">
            <div className="quiz-analytics-block">
              <p className="quiz-analytics-block-title">Top matches</p>
              <ul>
                {Object.entries(quizStats.by_top_service)
                  .sort((a, b) => b[1] - a[1])
                  .map(([label, count]) => (
                    <li key={label}>
                      <span>{label}</span>
                      <strong>{count}</strong>
                    </li>
                  ))}
              </ul>
            </div>
            <div className="quiz-analytics-block">
              <p className="quiz-analytics-block-title">Who's visiting</p>
              <ul>
                {Object.entries(quizStats.by_audience)
                  .sort((a, b) => b[1] - a[1])
                  .map(([label, count]) => (
                    <li key={label}>
                      <span>{label}</span>
                      <strong>{count}</strong>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Filters */}
      <div className="admin-toolbar">
        <div className="admin-search">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by name, email, phone, or ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="admin-search-input"
          />
        </div>
        <div className="admin-filters">
          {TYPE_FILTERS.map((f) => (
            <button
              key={f.value || 'all-types'}
              onClick={() => setTypeFilter(f.value)}
              className={`admin-filter-chip ${typeFilter === f.value ? 'active' : ''}`}
              data-testid={`type-filter-${f.value || 'all'}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="admin-filters">
          {PAYMENT_FILTERS.map((f) => (
            <button
              key={f.value || 'all'}
              onClick={() => setPaymentFilter(f.value)}
              className={`admin-filter-chip ${paymentFilter === f.value ? 'active' : ''}`}
              data-testid={`filter-${f.value || 'all'}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="admin-table-wrap">
        {loading ? (
          <div className="admin-empty">
            <div className="spinner" />
            <p>Loading submissions…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="admin-empty">
            <p>No submissions match your filters.</p>
          </div>
        ) : (
          <table className="admin-table" data-testid="admin-submissions-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Name</th>
                <th>Contact</th>
                <th>Issue / Program</th>
                <th>Payment</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => {
                const d = contactOf(s);
                const isCarebnb = s.type === 'carebnb';
                return (
                  <tr key={s.id} data-testid={`submission-row-${s.id}`}>
                    <td>
                      <span className={`badge type-${s.type || 'intake'}`} data-testid={`type-badge-${s.id}`}>
                        {s.type === 'carebnb' ? 'CareBnB'
                          : s.type === 'carebnb_gift' ? 'CareBnB Gift'
                          : s.type === 'health_coaching' ? 'Health Coaching'
                          : s.type === 'momshiach_method' ? 'Momshiach Method'
                          : s.type === 'lead_magnet' ? 'Lead Magnet'
                          : 'Energy Therapy'}
                      </span>
                    </td>
                    <td>
                      <strong>{d.firstName} {d.lastName}</strong>
                      {d.hebrewName && <div className="cell-sub">{d.hebrewName}</div>}
                      {isCarebnb && d.numGuests === '2' && d.partnerFirstName && (
                        <div className="cell-sub">+ {d.partnerFirstName} {d.partnerLastName}</div>
                      )}
                    </td>
                    <td>
                      <div>{d.email || '—'}</div>
                      <div className="cell-sub">{d.phone || d.whatsapp || '—'}</div>
                    </td>
                    <td className="cell-clip">
                      {s.type === 'carebnb'
                        ? `Shabbat Chavaya · ${d.numGuests || 1} guest${d.numGuests === '2' ? 's' : ''}${d.preferredDate ? ' · ' + d.preferredDate : ''}`
                        : s.type === 'health_coaching'
                        ? (d.goals && d.goals.length ? `Goals: ${d.goals.slice(0, 2).join(', ')}${d.goals.length > 2 ? '…' : ''}` : (d.biggestStruggle || '—'))
                        : s.type === 'momshiach_method'
                        ? `Momshiach Method · ${d.planLabel || d.plan || 'full'}`
                        : s.type === 'carebnb_gift'
                        ? 'Shabbat Chavaya gift'
                        : (d.specificIssue || '—')}
                    </td>
                    <td>
                      <span className={`badge ${statusBadgeClass(s.payment_status)}`}>
                        {s.payment_status || 'not_started'}
                      </span>
                    </td>
                    <td data-testid={`amount-cell-${s.id}`}>
                      {s.payment ? (
                        <>
                          <strong>{fmtMoney(s.payment.amount_paid, s.payment.currency)}</strong>
                          <div className="cell-sub">
                            of {fmtMoney(s.payment.amount_due, s.payment.currency)}
                            {s.payment.discount_pct > 0 && <span className="badge badge-discount">−{Math.round(s.payment.discount_pct * 100)}%</span>}
                          </div>
                        </>
                      ) : <span className="cell-sub">—</span>}
                    </td>
                    <td>
                      <span className="badge status-admin">{s.admin_status || 'new'}</span>
                    </td>
                    <td className="cell-sub">
                      {s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td>
                      <button
                        onClick={() => openDetails(s)}
                        className="btn-secondary btn-sm"
                        data-testid={`view-submission-${s.id}`}
                      >
                        <Eye size={14} /> View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Drawer */}
      {selected && (
        <div className="admin-drawer-overlay" onClick={closeDetails}>
          <div className="admin-drawer" onClick={(e) => e.stopPropagation()} data-testid="submission-detail-drawer">
            <div className="admin-drawer-header">
              <div>
                <h2 className="heading-2">{selected.data?.firstName} {selected.data?.lastName}</h2>
                <p className="caption">ID: {selected.id}</p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => downloadPdf(selected.id)}
                  className="btn-secondary btn-sm"
                  data-testid="download-pdf-btn"
                >
                  <Download size={14} /> PDF
                </button>
                <button onClick={closeDetails} className="btn-close" data-testid="close-drawer-btn">
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="admin-drawer-body">
              {/* Status controls */}
              <div className="detail-section">
                <h3 className="heading-3">Admin Status</h3>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {STATUS_OPTIONS.map((opt) => {
                    const Icon = opt.icon;
                    const active = selected.admin_status === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => updateStatus(opt.value)}
                        disabled={saving}
                        className={`admin-filter-chip ${active ? 'active' : ''}`}
                        data-testid={`status-${opt.value}`}
                      >
                        <Icon size={14} /> {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Notes */}
              <div className="detail-section">
                <h3 className="heading-3">Notes</h3>
                <textarea
                  className="form-textarea"
                  rows="4"
                  placeholder="Add private notes about this client…"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="admin-notes-textarea"
                />
                <button
                  onClick={saveNotes}
                  disabled={saving}
                  className="btn-primary btn-sm"
                  style={{ marginTop: 8 }}
                  data-testid="save-notes-btn"
                >
                  <Save size={14} /> Save Notes
                </button>
              </div>

              {/* Photo */}
              {selected.data?.photoId && (
                <div className="detail-section">
                  <h3 className="heading-3">Photo</h3>
                  <img
                    src={photoUrl(selected.data.photoId)}
                    alt="Client"
                    className="admin-photo"
                    data-testid="submission-photo"
                  />
                </div>
              )}

              {/* Payment */}
              <div className="detail-section">
                <h3 className="heading-3">Payment</h3>
                <p><strong>Status:</strong> <span className={`badge ${statusBadgeClass(selected.payment_status)}`}>{selected.payment_status}</span></p>
                {selected.payment && <PaymentBreakdown p={selected.payment} />}
                {selected.paypal_order_id && (
                  <p className="body-small" data-testid="admin-paypal-order">
                    <strong>PayPal order:</strong> {selected.paypal_order_id} · {selected.paypal_status || 'awaiting payment'}
                  </p>
                )}
                {selected.paypal_subscription_id && (
                  <p className="body-small" data-testid="admin-paypal-info">
                    <strong>PayPal subscription:</strong> {selected.paypal_subscription_id} · {selected.paypal_status || 'APPROVAL_PENDING'} · {selected.payments_completed ?? 0} of {selected.data?.installments} payments made
                  </p>
                )}
                {selected.stripe_session_id && (
                  <p className="body-small"><strong>Stripe session:</strong> {selected.stripe_session_id}</p>
                )}
              </div>

              {/* Full form data */}
              <div className="detail-section">
                <h3 className="heading-3">Intake Responses</h3>
                <dl className="detail-list">
                  {Object.entries(selected.data || {}).filter(([k]) => !['photoId', 'photo', 'photoPreview'].includes(k)).map(([k, v]) => (
                    <React.Fragment key={k}>
                      <dt>{k}</dt>
                      <dd>{Array.isArray(v) ? v.join(', ') : (typeof v === 'boolean' ? (v ? 'Yes' : 'No') : (v || '—'))}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;

